import type { FunctionComponent } from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";

import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { DefaultCell } from "pro-features/components/Workspace/TableCells/DefaultCell";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { translate } from "app/filters/i18nFilter";

const BRIGHT_TEXT_PERCENT_THRESHOLD = 80;
const TABLE_DEFAULT_MAX_PERCENT = 100;
const TABLE_DEFAULT_MIN_PERCENT = 0;

const CellContainer = styled(DefaultCell)`
    display: flex;
    justify-content: flex-end;
`;

/**
 * The cell's background color.
 * background opacity is used to set how saturated the cell color is
 * the higher the percentage value of the cell - the more saturated the cell is.
 * this way we create a "heatmap" effect.
 */
const CellBackground = styled.div<{ opacityPercent: number; backgroundColor: string }>`
    position: absolute;
    background-color: ${({ backgroundColor }) => backgroundColor};
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    z-index: -1;
    opacity: ${({ opacityPercent }) => opacityPercent + "%"};
`;

/**
 * Percentage text should be bright / dark according to the background color opacity. design has decided that
 * text should appear as white when the background opacity is above 80%, and dark otherwise.
 * text should also appear as white when the current row / cell is on focus/hover.
 */
const PercentageText = styled.span<{ isBrightText: boolean; lineHeight: string }>`
    font-size: 14px;
    color: ${({ isBrightText }) => (isBrightText ? colorsPalettes.carbon[0] : colorsPalettes.carbon[500])};
    z-index: 1;
    line-height: ${({ lineHeight }) => lineHeight};

    ${CellContainer}:hover &, .active-row & {
        color: ${({ isBrightText }) => (isBrightText ? "inherit" : colorsPalettes.carbon[500])};
    }
`;

/**
 * We want to set the cells with the highest/lowest percentage values on the table
 * to be the most/least saturated cells. for this reason - we normalize the percentage
 * values to between 0% to 100%. the normalized value will be the background's opacity.
 * for normalization formula, see: https://stats.stackexchange.com/questions/70801/how-to-normalize-data-to-0-1-range
 */
const normalizeCellOpacity = (cellValue: number, maxValueOnTable: number, minValueOnTable: number) => {
    const normalizedValue = (cellValue - minValueOnTable) / (maxValueOnTable - minValueOnTable);
    return normalizedValue * 100;
};

interface HeatmapCellProps extends ITableCellProps {
    backgroundColor?: string;
    useOpacity?: boolean;
    opacityPrecentage?: number;
    shouldBrightText?: boolean;
    lineHeight?: string;
}

export const HeatmapCell: FunctionComponent<HeatmapCellProps> = ({
    value,
    tableMetadata,
    hideZeroValue,
    replaceZeroValue,
    CellValueFormat = "percentagesign",
    backgroundColor = colorsPalettes.blue[400],
    useOpacity = true,
    opacityPrecentage = 0,
    shouldBrightText = true,
    lineHeight = "inherit",
}) => {
    const hasValue = typeof value !== "undefined" && value && value !== 0;
    const shouldHideValue = hideZeroValue && !hasValue;
    const maxValueOnTable = tableMetadata.maxCellValue ?? TABLE_DEFAULT_MAX_PERCENT;
    const minValueOnTable = tableMetadata.minCellValue ?? TABLE_DEFAULT_MIN_PERCENT;

    const { backgroundOpacity, shouldUseBrightText } = useMemo(() => {
        const opacityValue =
            hasValue && useOpacity ? normalizeCellOpacity(value, maxValueOnTable, minValueOnTable) : opacityPrecentage;

        return {
            backgroundOpacity: opacityValue,
            shouldUseBrightText: shouldBrightText && opacityValue > BRIGHT_TEXT_PERCENT_THRESHOLD,
        };
    }, [value, maxValueOnTable, minValueOnTable]);

    const displayPercentText = useMemo(() => {
        return shouldHideValue
            ? translate("common.n/a")
            : value === 0 && replaceZeroValue
              ? replaceZeroValue
              : dynamicFilterFilter()(value, CellValueFormat);
    }, [value]);

    return (
        <CellContainer>
            <PercentageText
                isBrightText={shouldUseBrightText}
                lineHeight={lineHeight}
            >
                {displayPercentText}
            </PercentageText>

            <CellBackground
                opacityPercent={backgroundOpacity}
                backgroundColor={backgroundColor}
            />
        </CellContainer>
    );
};
HeatmapCell.displayName = "HeatmapCell";
